import { faPercent } from '@fortawesome/pro-solid-svg-icons';
import { useInstantSearch } from 'react-instantsearch';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { FilterTitle } from '@sorare/core/src/components/search/FilterTitle';
import { FILTERS, FilterWidget } from '@sorare/core/src/lib/filters';
import { filters } from '@sorare/core/src/lib/glossary';

import { makeRangeFilter } from 'searchCards/RangeSlider';

import RangeSlider from '../../search/RangeSlider';

const AppearancesTitle = () => {
  const { indexUiState } = useInstantSearch();

  const so5L5App = indexUiState.range?.[FILTERS.lastFiveAppearances.attribute];
  const so5L15App =
    indexUiState.range?.[FILTERS.lastFifteenAppearances.attribute];

  const selected =
    (so5L5App &&
    so5L5App !==
      `${FILTERS.lastFiveAppearances.min}:${FILTERS.lastFiveAppearances.max}`
      ? 1
      : 0) +
    (so5L15App &&
    so5L15App !==
      `${FILTERS.lastFifteenAppearances.min}:${FILTERS.lastFifteenAppearances.max}`
      ? 1
      : 0);

  return <FilterTitle name={filters.appearances} selected={selected} />;
};

const Root = styled(Vertical).attrs({ gap: 3 })``;
const Section = styled(Vertical)`
  &:not(:has(.FilterWidget.visible)) {
    display: none;
  }
`;

const AppearancesComponent = ({
  fiveAppearancesWidget,
  fifteenAppearancesWidget,
  visible,
}: {
  fiveAppearancesWidget: FilterWidget;
  fifteenAppearancesWidget: FilterWidget;
  visible: boolean;
}) => {
  return (
    <Root>
      <Section>
        <LabelM color="var(--c-neutral-600)">
          <FormattedMessage {...FILTERS.lastFiveAppearances.title} />
        </LabelM>
        {fiveAppearancesWidget.Component({ visible })}
      </Section>
      <Section>
        <LabelM color="var(--c-neutral-600)">
          <FormattedMessage {...FILTERS.lastFifteenAppearances.title} />
        </LabelM>
        {fifteenAppearancesWidget.Component({ visible })}
      </Section>
    </Root>
  );
};

export const RefineAppearances = (): FilterWidget => {
  const interval5 = 100 / FILTERS.lastFiveAppearances.max;
  const interval15 = 100 / FILTERS.lastFifteenAppearances.max;

  const fiveAppearancesWidget = makeRangeFilter(
    ({ visible }) => (
      <RangeSlider
        attribute={FILTERS.lastFiveAppearances.attribute}
        minForced={FILTERS.lastFiveAppearances.min}
        maxForced={FILTERS.lastFiveAppearances.max}
        toInput={v => Math.round(v * interval5).toString()}
        fromInput={v => v / interval5}
        icon={<FontAwesomeIcon icon={faPercent} />}
        visible={visible}
      />
    ),
    FILTERS.lastFiveAppearances
  );

  const fifteenAppearancesWidget = makeRangeFilter(
    ({ visible }) => (
      <RangeSlider
        attribute={FILTERS.lastFifteenAppearances.attribute}
        minForced={FILTERS.lastFifteenAppearances.min}
        maxForced={FILTERS.lastFifteenAppearances.max}
        toInput={v => Math.round(v * interval15).toString()}
        fromInput={v => v / interval15}
        icon={<FontAwesomeIcon icon={faPercent} />}
        visible={visible}
      />
    ),
    FILTERS.lastFifteenAppearances
  );

  return {
    key: FILTERS.appearances.attribute,
    type: 'range',
    attribute: FILTERS.appearances.attribute,
    title: <AppearancesTitle />,
    Component: ({ visible }) => (
      <AppearancesComponent
        fiveAppearancesWidget={fiveAppearancesWidget}
        fifteenAppearancesWidget={fifteenAppearancesWidget}
        visible={visible}
      />
    ),
  };
};
